import { ScrollView, View, Modal } from "react-native";
import React, { useMemo } from "react";
import {
  PremiumCodeDetails,
  PremiumInventoryDetails,
  SectionDetails,
} from "features/types";
import { Divider, ListItem, Text } from "@ui-kitten/components";
import Spacer from "components/Spacer";
import Button from "components/Button";
import { useAppDispatch } from "hooks/useAppDispatch";
import {
  setPremiumInventory,
  setSelectedPremiumCode,
} from "features/slices/reservationFlowSlice";
import { useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { ScheduleStackParamList } from "navigation/types";
import MainContainer from "components/MainContainer";
import VenuSeatMap from "./VenueSeatMap";
import { useAppSelector } from "hooks/useAppSelector";
import usePreferredSections from "hooks/usePreferredSections";
import { Image } from "expo-image";
import { formatCurrency } from "utils/appUtils";

interface Props {
  premiumInventory?: PremiumInventoryDetails;
  visible: boolean;
  onDismiss: () => void;
  isMarketingAssets?: boolean;
}

const PremiumCodeSelectorModal: React.FC<Props> = (props) => {
  const { premiumInventory, visible, onDismiss, isMarketingAssets } = props;

  const { tenantSubdomain, tenantType, tenantThemeColor } = useAppSelector(
    (state) => state.tenant
  );

  const { selectedEvent, selectedSubscription } = useAppSelector(
    (state) => state.reservationFlow
  );
  const {
    preferredSections,
    setPreferredSections,
    handleAddPreferredSection,
    handleRemovePreferredSection,
  } = usePreferredSections();
  const dispatch = useAppDispatch();
  const navigation =
    useNavigation<
      NativeStackNavigationProp<ScheduleStackParamList, "SeatSelection">
    >();

  const handleViewInventory = (
    premiumInventory: PremiumInventoryDetails,
    premiumCode: PremiumCodeDetails
  ) => {
    dispatch(setPremiumInventory(premiumInventory));
    dispatch(setSelectedPremiumCode(premiumCode));
    onDismiss();
    navigation.navigate("PremiumCodeDetails");
  };

  // go through each premium code in premium inventory,
  // and compare each section against preferredSections
  // add matched sections to the returned array
  const allSections = useMemo(() => {
    if (!premiumInventory) return [];
    const sections: SectionDetails[] = [];
    for (const premiumCode of premiumInventory.premiumCodes) {
      for (const section of premiumCode.sections) {
        sections.push(section);
      }
    }
    return sections;
  }, [premiumInventory, preferredSections]);

  // match each section within a premium code against preferredSections
  const filteredPremiumCodes = useMemo(() => {
    if (!premiumInventory) return [];
    const result: PremiumCodeDetails[] = [];
    for (const premiumCode of premiumInventory.premiumCodes) {
      for (const section of premiumCode.sections) {
        if (
          section.numAvailable > 0 &&
          (preferredSections.includes(section.section) ||
            preferredSections.length === 0)
        ) {
          result.push(premiumCode);
          break;
        }
      }
    }
    return result;
  }, [premiumInventory, preferredSections]);

  return (
    <Modal animationType="slide" visible={visible} onDismiss={onDismiss}>
      <MainContainer goBackAction={onDismiss}>
        <View>
          <View>
            <Text category="h4" style={{ textAlign: "center" }}>
              {premiumInventory?.premiumClass.name}
            </Text>
            <Spacer />
          </View>
          <View style={{ height: 300 }}>
            {isMarketingAssets ? (
              <Image
                source={selectedEvent!.imageUrl}
                style={{ width: "100%", height: "100%" }}
              />
            ) : (
              <VenuSeatMap
                tenantType={tenantType!}
                tenantSubdomain={tenantSubdomain!}
                theme={tenantThemeColor}
                selectedPartySize={0}
                eventType={selectedEvent!.eventType}
                venueName={selectedEvent!.venueName}
                blocks={allSections}
                seatGroups={
                  selectedSubscription!.planPackage.primaryComponent.seatGroups
                }
                mapId="premium-code-selector"
                onSectionAdded={handleAddPreferredSection}
                onSectionRemoved={handleRemovePreferredSection}
                preferredSections={preferredSections}
                onReset={() => setPreferredSections([])}
              />
            )}
            <Spacer />
          </View>
        </View>
        {premiumInventory !== undefined && (
          <ScrollView
            style={{
              marginHorizontal: "auto",
              height: 500,
              width: "100%",
            }}
          >
            <View>
              {filteredPremiumCodes.length === 0 && (
                <Text category="s1" style={{ textAlign: "center" }}>
                  Sorry, there are no available sections for your selection
                </Text>
              )}
              {filteredPremiumCodes.map((premiumCode) => {
                return (
                  <View key={premiumCode.code}>
                    <ListItem
                      onPress={() =>
                        handleViewInventory(premiumInventory, premiumCode)
                      }
                      title={premiumCode.name}
                      description={
                        <View>
                          {premiumInventory.premiumClass.type === "GA" && (
                            <>
                              {premiumCode.minPartySize ===
                              premiumCode.maxPartySize ? (
                                <Text category="c1">
                                  {isMarketingAssets
                                    ? `Quantity: ${premiumCode.minPartySize}`
                                    : `Seats ${premiumCode.minPartySize} people`}
                                </Text>
                              ) : (
                                <Text category="c1">
                                  Seats {premiumCode.minPartySize} to{" "}
                                  {premiumCode.maxPartySize} people
                                </Text>
                              )}
                            </>
                          )}
                          {premiumInventory.premiumClass.type === "SEATS" ? (
                            <Text category="s2">
                              Price:{" "}
                              {formatCurrency(premiumCode.minPlanTicketCharge, {
                                priceType: premiumInventory.priceType,
                              })}{" "}
                              (per person)
                            </Text>
                          ) : (
                            <Text category="s2">
                              Total Price:{" "}
                              {formatCurrency(premiumCode.minBundlePrice, {
                                priceType: premiumInventory.priceType,
                              })}
                            </Text>
                          )}
                        </View>
                      }
                      accessoryRight={() => (
                        <Button
                          size="small"
                          onPress={() =>
                            handleViewInventory(premiumInventory, premiumCode)
                          }
                        >
                          Book
                        </Button>
                      )}
                    />
                    <Divider />
                  </View>
                );
              })}
            </View>
          </ScrollView>
        )}
      </MainContainer>
    </Modal>
  );
};

export default PremiumCodeSelectorModal;
